import * as Sentry from '@sentry/react';
import { supabase } from '@shared/lib/supabase/client';
import type { Database } from '@shared/types/database';

type NewsletterAbonnement = Database['public']['Tables']['nieuwsbrief_abonnementen']['Row'];
type NewsletterAbonnementInsert = Database['public']['Tables']['nieuwsbrief_abonnementen']['Insert'];
type NewsletterType = Database['public']['Enums']['nieuwsbrief_type'];
type PortalType = Database['public']['Enums']['portal_type'];

export interface NewsletterSubscription {
  id: string;
  userId: string;
  email: string;
  type: NewsletterType;
  active: boolean;
  preferPdfAttachment: boolean;
  createdAt: string;
  updatedAt: string;
}

// Map database row to frontend interface
function mapToNewsletterSubscription(row: NewsletterAbonnement): NewsletterSubscription {
  return {
    id: row.id,
    userId: row.gebruiker_id,
    email: row.email,
    type: row.nieuwsbrief_type,
    active: row.actief,
    preferPdfAttachment: row.voorkeur_pdf_bijlage,
    createdAt: row.aangemaakt_op || '',
    updatedAt: row.bijgewerkt_op || '',
  };
}

class NewsletterService {
  /**
   * Get all newsletter subscriptions for a user (optionally filtered by portal)
   */
  async getSubscriptions(userId: string, portal?: PortalType): Promise<NewsletterSubscription[]> {
    return await Sentry.startSpan(
      {
        op: 'db.query',
        name: 'Get Newsletter Subscriptions',
        attributes: { 'user.id': userId, 'portal': portal },
      },
      async () => {
        try {
          let query = supabase
            .from('nieuwsbrief_abonnementen')
            .select('*')
            .eq('gebruiker_id', userId);

          // Filter by portal if provided (for multi-portal users)
          if (portal) {
            query = query.eq('portal', portal);
          }

          const { data, error } = await query;

          if (error) {
            console.error('Error fetching newsletter subscriptions:', error);
            Sentry.captureException(error);
            throw new Error(`Failed to fetch subscriptions: ${error.message}`);
          }

          return (data || []).map(mapToNewsletterSubscription);
        } catch (error) {
          console.error('Newsletter service error:', error);
          Sentry.captureException(error);
          throw error;
        }
      }
    );
  }

  /**
   * Subscribe to a newsletter
   */
  async subscribe(
    userId: string,
    email: string,
    type: NewsletterType,
    preferPdf: boolean,
    portal: PortalType
  ): Promise<NewsletterSubscription> {
    return await Sentry.startSpan(
      {
        op: 'db.mutation',
        name: 'Subscribe to Newsletter',
        attributes: {
          'user.id': userId,
          'newsletter.type': type,
        },
      },
      async () => {
        try {
          const insertData: NewsletterAbonnementInsert = {
            gebruiker_id: userId,
            email: email,
            nieuwsbrief_type: type,
            actief: true,
            voorkeur_pdf_bijlage: preferPdf,
            portal: portal,
          };

          const { data, error } = await supabase
            .from('nieuwsbrief_abonnementen')
            .insert(insertData)
            .select()
            .single();

          if (error) {
            // Handle unique constraint violation (already subscribed)
            if (error.code === '23505') {
              throw new Error('U bent al aangemeld voor deze nieuwsbrief');
            }
            console.error('Error subscribing to newsletter:', error);
            Sentry.captureException(error);
            throw new Error(`Aanmelden mislukt: ${error.message}`);
          }

          return mapToNewsletterSubscription(data);
        } catch (error) {
          console.error('Newsletter service error:', error);
          Sentry.captureException(error);
          throw error;
        }
      }
    );
  }

  /**
   * Unsubscribe from a newsletter (portal-specific for multi-portal users)
   */
  async unsubscribe(userId: string, type: NewsletterType, portal: PortalType): Promise<void> {
    return await Sentry.startSpan(
      {
        op: 'db.mutation',
        name: 'Unsubscribe from Newsletter',
        attributes: {
          'user.id': userId,
          'newsletter.type': type,
          'portal': portal,
        },
      },
      async () => {
        try {
          const { error } = await supabase
            .from('nieuwsbrief_abonnementen')
            .delete()
            .eq('gebruiker_id', userId)
            .eq('nieuwsbrief_type', type)
            .eq('portal', portal); // CRITICAL: Only unsubscribe from specific portal

          if (error) {
            console.error('Error unsubscribing from newsletter:', error);
            Sentry.captureException(error);
            throw new Error(`Afmelden mislukt: ${error.message}`);
          }
        } catch (error) {
          console.error('Newsletter service error:', error);
          Sentry.captureException(error);
          throw error;
        }
      }
    );
  }

  /**
   * Update newsletter preferences (PDF attachment preference, portal-specific)
   */
  async updatePreferences(
    userId: string,
    type: NewsletterType,
    preferPdf: boolean,
    portal: PortalType
  ): Promise<NewsletterSubscription> {
    return await Sentry.startSpan(
      {
        op: 'db.mutation',
        name: 'Update Newsletter Preferences',
        attributes: {
          'user.id': userId,
          'newsletter.type': type,
          'newsletter.prefer_pdf': preferPdf,
          'portal': portal,
        },
      },
      async () => {
        try {
          const { data, error } = await supabase
            .from('nieuwsbrief_abonnementen')
            .update({ voorkeur_pdf_bijlage: preferPdf })
            .eq('gebruiker_id', userId)
            .eq('nieuwsbrief_type', type)
            .eq('portal', portal) // Update only for specific portal
            .select()
            .single();

          if (error) {
            console.error('Error updating newsletter preferences:', error);
            Sentry.captureException(error);
            throw new Error(`Voorkeuren bijwerken mislukt: ${error.message}`);
          }

          if (!data) {
            throw new Error('Abonnement niet gevonden');
          }

          return mapToNewsletterSubscription(data);
        } catch (error) {
          console.error('Newsletter service error:', error);
          Sentry.captureException(error);
          throw error;
        }
      }
    );
  }

  /**
   * Toggle newsletter active state
   */
  async toggleSubscription(
    userId: string,
    type: NewsletterType,
    active: boolean
  ): Promise<NewsletterSubscription> {
    return await Sentry.startSpan(
      {
        op: 'db.mutation',
        name: 'Toggle Newsletter Subscription',
        attributes: {
          'user.id': userId,
          'newsletter.type': type,
          'newsletter.active': active,
        },
      },
      async () => {
        try {
          const { data, error } = await supabase
            .from('nieuwsbrief_abonnementen')
            .update({ actief: active })
            .eq('gebruiker_id', userId)
            .eq('nieuwsbrief_type', type)
            .select()
            .single();

          if (error) {
            console.error('Error toggling newsletter subscription:', error);
            Sentry.captureException(error);
            throw new Error(`Status wijzigen mislukt: ${error.message}`);
          }

          if (!data) {
            throw new Error('Abonnement niet gevonden');
          }

          return mapToNewsletterSubscription(data);
        } catch (error) {
          console.error('Newsletter service error:', error);
          Sentry.captureException(error);
          throw error;
        }
      }
    );
  }

  /**
   * Get subscription status for a specific newsletter type
   */
  async getSubscriptionStatus(
    userId: string,
    type: NewsletterType
  ): Promise<NewsletterSubscription | null> {
    try {
      const { data, error } = await supabase
        .from('nieuwsbrief_abonnementen')
        .select('*')
        .eq('gebruiker_id', userId)
        .eq('nieuwsbrief_type', type)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No subscription found
          return null;
        }
        console.error('Error checking subscription status:', error);
        Sentry.captureException(error);
        throw new Error(`Status ophalen mislukt: ${error.message}`);
      }

      return data ? mapToNewsletterSubscription(data) : null;
    } catch (error) {
      console.error('Newsletter service error:', error);
      Sentry.captureException(error);
      throw error;
    }
  }
}

export const newsletterService = new NewsletterService();
